/**
 * PerformancesService
 *
 * Dancer-self-published performances (galas, tours, workshops, appearances,
 * generic performances). Independent of the `cast_assignments` flow.
 *
 * Auth & ownership are enforced by RLS — see migration
 * `20260516101500_create_performances_table.sql`. This service is intentionally
 * thin: every method translates one Supabase operation into a `Result<T>`
 * with structured logging. It does NOT re-check ownership — the database
 * is the single source of truth for "can this user write this row".
 *
 * Related: WIP_adding_dancer_published_performances_2026_05_16.md
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

export type PerformanceType =
  | 'performance'
  | 'gala'
  | 'tour'
  | 'workshop'
  | 'appearance';

export interface Performance {
  id: string;
  professional_profile_id: string;
  title: string;
  type: PerformanceType;
  description: string | null;
  start_date: string; // ISO YYYY-MM-DD
  end_date: string | null;
  venue: string | null;
  city: string | null;
  country_code: string | null;
  ticket_url: string | null;
  choreographer: string | null;
  // BAL-248 §10b: optional role/part for this performance (e.g. "Kitri").
  // Column added 20260604000000. Editor write-path is a follow-up (the
  // performances form is its own edit surface).
  role: string | null;
  comment: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PerformanceInsert {
  professional_profile_id: string;
  title: string;
  type?: PerformanceType;
  description?: string | null;
  start_date: string;
  end_date?: string | null;
  venue?: string | null;
  city?: string | null;
  country_code?: string | null;
  ticket_url?: string | null;
  choreographer?: string | null;
  role?: string | null;
  comment?: string | null;
}

export interface PerformanceUpdate {
  title?: string;
  type?: PerformanceType;
  description?: string | null;
  start_date?: string;
  end_date?: string | null;
  venue?: string | null;
  city?: string | null;
  country_code?: string | null;
  ticket_url?: string | null;
  choreographer?: string | null;
  role?: string | null;
  comment?: string | null;
}

export class PerformancesService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * List all performances for a profile. Order: start_date DESC (newest first).
   * Returns Result.ok([]) when there are no rows — empty is a normal state.
   */
  async listForProfile(
    professionalProfileId: string,
  ): Promise<Result<Performance[]>> {
    const logContext = { method: 'listForProfile', professionalProfileId };
    void this.log('info', 'Fetching performances for profile', logContext);

    try {
      const { data, error } = await fetchAllRows((from, to) =>
        this.client
          .from('performances')
          .select('*')
          .eq('professional_profile_id', professionalProfileId)
          .order('start_date', { ascending: false })
          .order('id')
          .range(from, to),
      );

      if (error) {
        void this.log('error', 'Failed to fetch performances', {
          ...logContext,
          error: error.message,
          errorCode: 'code' in error ? error.code : undefined,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok((data ?? []) as Performance[]);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'listForProfile',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * List ONLY upcoming performances (end_date >= today OR start_date >= today
   * if no end_date). Order: start_date ASC (soonest first).
   */
  async listUpcomingForProfile(
    professionalProfileId: string,
  ): Promise<Result<Performance[]>> {
    const todayIso = new Date().toISOString().slice(0, 10);
    const logContext = {
      method: 'listUpcomingForProfile',
      professionalProfileId,
      todayIso,
    };
    void this.log('info', 'Fetching upcoming performances', logContext);

    try {
      // A performance is "upcoming" while its last day is today or in the
      // future. For single-day events end_date is null, so we fall back to
      // start_date.
      const { data, error } = await fetchAllRows((from, to) =>
        this.client
          .from('performances')
          .select('*')
          .eq('professional_profile_id', professionalProfileId)
          // PostgREST: chain or() so both clauses share an OR group
          .or(
            `end_date.gte.${todayIso},and(end_date.is.null,start_date.gte.${todayIso})`,
          )
          .order('start_date', { ascending: true })
          .order('id')
          .range(from, to),
      );

      if (error) {
        void this.log('error', 'Failed to fetch upcoming performances', {
          ...logContext,
          error: error.message,
          errorCode: 'code' in error ? error.code : undefined,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok((data ?? []) as Performance[]);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'listUpcomingForProfile',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * List ONLY past performances. Order: start_date DESC (most recent first).
   * Capped at `limit` (default 24, matches `get_public_featured_events`).
   */
  async listPastForProfile(
    professionalProfileId: string,
    limit = 24,
  ): Promise<Result<Performance[]>> {
    const todayIso = new Date().toISOString().slice(0, 10);
    const logContext = {
      method: 'listPastForProfile',
      professionalProfileId,
      todayIso,
      limit,
    };
    void this.log('info', 'Fetching past performances', logContext);

    try {
      // "Past" = the event has already ended. end_date < today if set,
      // otherwise start_date < today.
      const { data, error } = await this.client
        .from('performances')
        .select('*')
        .eq('professional_profile_id', professionalProfileId)
        .or(
          `end_date.lt.${todayIso},and(end_date.is.null,start_date.lt.${todayIso})`,
        )
        .order('start_date', { ascending: false })
        .limit(limit);

      if (error) {
        void this.log('error', 'Failed to fetch past performances', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok((data ?? []) as Performance[]);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'listPastForProfile',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  async getById(id: string): Promise<Result<Performance | null>> {
    const logContext = { method: 'getById', id };
    void this.log('info', 'Fetching performance by id', logContext);

    try {
      const { data, error } = await this.client
        .from('performances')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) {
        void this.log('error', 'Failed to fetch performance', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok((data as Performance | null) ?? null);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getById',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  async create(
    input: PerformanceInsert,
    createdBy: string | null,
  ): Promise<Result<Performance>> {
    const logContext = {
      method: 'create',
      professionalProfileId: input.professional_profile_id,
      type: input.type ?? 'performance',
    };
    void this.log('info', 'Creating performance', logContext);

    try {
      const { data, error } = await this.client
        .from('performances')
        .insert({
          professional_profile_id: input.professional_profile_id,
          title: input.title,
          type: input.type ?? 'performance',
          description: input.description ?? null,
          start_date: input.start_date,
          end_date: input.end_date ?? null,
          venue: input.venue ?? null,
          city: input.city ?? null,
          country_code: input.country_code ?? null,
          ticket_url: input.ticket_url ?? null,
          choreographer: input.choreographer ?? null,
          role: input.role ?? null,
          comment: input.comment ?? null,
          created_by: createdBy,
        })
        .select('*')
        .single();

      if (error) {
        void this.log('error', 'Failed to create performance', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        // RLS denial surfaces as 42501. Surface as a clean forbidden error so
        // the action layer can show "You don't have access" rather than
        // generic database failure.
        if (error.code === '42501') {
          return Result.fail(
            ServiceError.forbidden(
              'You do not have permission to add a performance to this profile',
            ),
          );
        }
        return Result.fail(ServiceError.database(error.message, error));
      }

      void this.log('info', 'Performance created successfully', {
        ...logContext,
        id: data.id,
      });
      return Result.ok(data as Performance);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'create',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  async update(
    id: string,
    input: PerformanceUpdate,
  ): Promise<Result<Performance>> {
    const logContext = { method: 'update', id, fields: Object.keys(input) };
    void this.log('info', 'Updating performance', logContext);

    try {
      // Only send fields the caller actually passed — partial update.
      const updatePayload: Database['public']['Tables']['performances']['Update'] =
        {};
      if (input.title !== undefined) updatePayload.title = input.title;
      if (input.type !== undefined) updatePayload.type = input.type;
      if (input.description !== undefined)
        updatePayload.description = input.description;
      if (input.start_date !== undefined)
        updatePayload.start_date = input.start_date;
      if (input.end_date !== undefined) updatePayload.end_date = input.end_date;
      if (input.venue !== undefined) updatePayload.venue = input.venue;
      if (input.city !== undefined) updatePayload.city = input.city;
      if (input.country_code !== undefined)
        updatePayload.country_code = input.country_code;
      if (input.ticket_url !== undefined)
        updatePayload.ticket_url = input.ticket_url;
      if (input.choreographer !== undefined)
        updatePayload.choreographer = input.choreographer;
      if (input.role !== undefined) updatePayload.role = input.role;
      if (input.comment !== undefined) updatePayload.comment = input.comment;

      const { data, error } = await this.client
        .from('performances')
        .update(updatePayload)
        .eq('id', id)
        .select('*')
        .single();

      if (error) {
        void this.log('error', 'Failed to update performance', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        if (error.code === '42501') {
          return Result.fail(
            ServiceError.forbidden(
              'You do not have permission to edit this performance',
            ),
          );
        }
        if (error.code === 'PGRST116') {
          return Result.fail(
            ServiceError.notFound(`Performance ${id} not found`, { id }),
          );
        }
        return Result.fail(ServiceError.database(error.message, error));
      }

      void this.log('info', 'Performance updated successfully', {
        ...logContext,
      });
      return Result.ok(data as Performance);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'update',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  async delete(id: string): Promise<Result<{ id: string }>> {
    const logContext = { method: 'delete', id };
    void this.log('info', 'Deleting performance', logContext);

    try {
      const { error } = await this.client
        .from('performances')
        .delete()
        .eq('id', id);

      if (error) {
        void this.log('error', 'Failed to delete performance', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        if (error.code === '42501') {
          return Result.fail(
            ServiceError.forbidden(
              'You do not have permission to delete this performance',
            ),
          );
        }
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok({ id });
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'delete',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }
}

export function createPerformancesService(
  client: SupabaseClient<Database>,
): PerformancesService {
  return new PerformancesService(client);
}
